import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Paper,
} from '@mui/material';
import {
  Grass,
  PersonAdd,
  ArrowForward,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

interface WebsiteSettings {
  siteLogo?: string;
  siteName?: string;
}

const UserTypeSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const [websiteSettings, setWebsiteSettings] = useState<WebsiteSettings | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/website-settings`);
        if (response.data.success) {
          setWebsiteSettings(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching website settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleSelectUserType = (userType: 'customer' | 'provider') => {
    navigate('/register', { state: { userType } });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: websiteSettings?.siteLogo 
          ? `linear-gradient(rgba(46, 125, 50, 0.88), rgba(27, 94, 32, 0.88)), url("${websiteSettings.siteLogo}")`
          : 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        py: 4,
      }}
    >
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3, backgroundColor: 'rgba(255, 255, 255, 0.98)' }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
              Welcome to LawnHub!
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Let's get you started. How would you like to use LawnHub? Accounts are free.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {/* Customer Option */}
            <Grid item xs={12} md={6}>
              <Card
                elevation={4}
                sx={{
                  height: '100%',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 8,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleSelectUserType('customer')}
                  sx={{ height: '100%', p: 3 }}
                >
                  <CardContent>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          bgcolor: 'success.light',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 2,
                        }}
                      >
                        <Grass sx={{ fontSize: 40, color: 'white' }} />
                      </Box>
                      <Typography variant="h5" gutterBottom fontWeight="bold" color="primary.main">
                        I Need Lawn Care
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        Get fast, reliable lawn care from trusted local providers
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          Create Free Customer Account
                        </Typography>
                        <ArrowForward fontSize="small" color="primary" />
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>

            {/* Provider Option */}
            <Grid item xs={12} md={6}>
              <Card
                elevation={4}
                sx={{
                  height: '100%',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 8,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleSelectUserType('provider')}
                  sx={{ height: '100%', p: 3 }}
                >
                  <CardContent>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          bgcolor: 'primary.light',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 2,
                        }}
                      >
                        <PersonAdd sx={{ fontSize: 40, color: 'white' }} />
                      </Box>
                      <Typography variant="h5" gutterBottom fontWeight="bold" color="primary.main">
                        I Provide Lawn Services
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        Grow your lawn care business with new customers in your service area. Manage everything in one place.
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          Create Free Provider Account
                        </Typography>
                        <ArrowForward fontSize="small" color="primary" />
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          </Grid>

          {/* Platform Services Reference */}
          <Box sx={{ textAlign: 'center', mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '1.3em' }}>
              <strong>LawnHub offers year-round services:</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.3em' }}>
              Lawn Mowing/Trimming • Weed Control & Fertilizing • Snow Removal
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '1.105rem', fontStyle: 'italic' }}>
              One platform for all your lawn care needs
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Typography
                component="span"
                color="primary"
                sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                onClick={() => navigate('/login')}
              >
                Sign In
              </Typography>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default UserTypeSelectionPage;

